import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import SwipableViews from 'react-swipeable-views';
import { TABLET_MIDDLE_WIDTH, TABLET_MIN_WIDTH } from '../../../../utils';
import cn from 'classnames';
import Airplane from '../../Airplane/Airplane';
import SeatMap from '../../SeatMap/SeatMap';
import { useTranslation } from 'react-i18next';
import { Seat, Segment, ServicesMap } from '../../../types';
import { GdsServiceById } from '@shared/lib/gdsService';
import { useTheme } from '../../../../theme';
import { default as BaseButton } from '../../../../BaseComponents/Button';
import { PassengerState } from '../../../../Checkout/store/passengers/reducers';
import { ModeContext } from '../../../../context';
import { Mode } from '../../../../types';
import Controls from '../Controls/Controls';
import { SeatsRecommendContext } from '../../../../Checkout/components/Checkout/SeatsRecommendation/SeatsRecommendation';
import { NULL_COMPONENT, useRenderers } from '../../../../renderProps';
import { RowWithParts } from '../../SeatMap/utils';
import { isPassengersHeightExceedViewport, isSegmentSeatMapTooWide } from './utils';
import InfoMessages from '../../../../InfoMessages';
import { InfoMessagePositionsEnum, SeatMapRestrictions } from '@websky/graphql';
import MobileDialog from '@shared/ui/MobileDialog/MobileDialog';

const MAP_PADDING = 280;

export interface Props {
	segments: Segment[];
	passengers: PassengerState[];
	services: ServicesMap;
	allSeatServices: ServicesMap;
	gdsServiceById: GdsServiceById;
	onSelect: (Seat: Seat[], passengerId: string) => void;
	onClear?: () => void;
	segmentIndex: number;
	passengerId: string;
	onSelectNextFlight: () => void;
	swipeBetweenSegments?: boolean;
	activeDoubleSeats?: boolean;
	isReturnFlight?: boolean;
	seatMapRef?: React.RefObject<HTMLDivElement>;
	dialogRef?: React.RefObject<HTMLDivElement>;
	fullScreen?: boolean;
	isNewServicePredicate?: (row: RowWithParts, nextRow: RowWithParts) => boolean;
	onProceed: () => void;
	onBack?: () => void;
	activeFilters?: SeatMapRestrictions[];

	renderPassengers: () => React.ReactElement;
	renderSeatsInfo: () => React.ReactElement;
}

const SeatMapServiceContent = React.memo<Props>(props => {
	const { t } = useTranslation('SeatMap');
	const theme = useTheme('SeatMap').ComponentStyles;

	const unavailableMessageRef = React.useRef<HTMLDivElement>();
	const [seatsOpen, setSeatsOpen] = React.useState(false);
	const [mapMinHeight, setMapMinHeight] = React.useState(0);

	const mode = useContext(ModeContext);
	const seatsRecommendContext = useContext(SeatsRecommendContext);
	const isMobile = useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });

	const { seatsInfoAdditionalSlot: SeatsInfoAdditionalSlotOverride = NULL_COMPONENT } = useRenderers();

	const isActiveDoubleSeat = useMemo(() => props.activeDoubleSeats && !seatsRecommendContext.isOpen, [
		props.activeDoubleSeats,
		seatsRecommendContext.isOpen
	]);

	const { currentSegment, isSeatMapTooWide } = useMemo(() => {
		const currentSegment = props.segments[props.segmentIndex];

		return { currentSegment, isSeatMapTooWide: isSegmentSeatMapTooWide(currentSegment) };
	}, [props.segmentIndex, props.segments]);

	const isSeatMapAvailable = useCallback((segmentIndex: number) => props.segments[segmentIndex]?.decks.length > 0, [
		props.segments
	]);

	const headerFixedClassNames = theme.header_fixed.split(' ');

	const canShowSeatMap = useCallback(
		(segment?: number) => {
			if (typeof segment !== 'undefined' && mode !== Mode.Checkin) {
				return !props.segments[segment].isSeatsAvailableInCheckinOnly;
			}

			return true;
		},
		[props.segments, mode]
	);

	useEffect(() => {
		if (isSeatMapAvailable(props.segmentIndex) || !unavailableMessageRef?.current) {
			return;
		}

		unavailableMessageRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
	}, [props.segmentIndex]);

	const fixPassengersOnScroll = useCallback(() => {
		const element = document.getElementById('passengers') as HTMLDivElement;
		const passengersContainer = document.getElementById('passengers-container') as HTMLDivElement;

		if (element && passengersContainer) {
			const elementRect = element.getBoundingClientRect();
			const passengersRect = passengersContainer.getBoundingClientRect();

			if (elementRect.top < (isMobile ? 0 : 8) && !isPassengersHeightExceedViewport(elementRect)) {
				headerFixedClassNames.forEach(className => element.classList.add(className));
			} else if (elementRect.top < passengersRect.top || isPassengersHeightExceedViewport(elementRect)) {
				headerFixedClassNames.forEach(className => element.classList.remove(className));
			}
		}
	}, [isMobile, headerFixedClassNames]);

	useEffect(() => {
		let timeout: NodeJS.Timeout;
		const element = document.getElementById('passengers');

		if (element) {
			timeout = setTimeout(() => {
				const helpersHeight = Math.ceil(element.getBoundingClientRect().height) + MAP_PADDING;
				setMapMinHeight(prev => (prev !== helpersHeight ? helpersHeight : prev));
			}, 0);
		}

		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [props.segmentIndex]);

	useEffect(() => {
		const element = document.getElementById('passengers');
		const scrollElement = props.dialogRef?.current ?? window;

		scrollElement.scrollTo({ top: 0, behavior: 'smooth' });

		if (element) {
			headerFixedClassNames.forEach(className => element.classList.remove(className));

			scrollElement.addEventListener('scroll', fixPassengersOnScroll);
		}

		return () => scrollElement.removeEventListener('scroll', fixPassengersOnScroll);
	}, [props.segmentIndex, isMobile]);

	const availableOnCheckinMessage = (
		<div className={theme.unavailable} ref={unavailableMessageRef}>
			{currentSegment && (
				<img
					alt="company icon"
					className={theme.unavailable__img}
					src={currentSegment.segmentInfo.operatingAirline.icon}
				/>
			)}

			<p className={theme.unavailable__title}>
				<p className={theme.unavailable__header}>{t('SeatMap:Less than 24 hours left before the flight')}</p>
				{t('SeatMap:You can select seats using the online registration service after issuing tickets')}
			</p>

			{props.segmentIndex + 1 < props.segments.length && (
				<BaseButton
					variant="optionAction"
					onClick={props.onSelectNextFlight}
					className={theme.unavailable__button}
				>
					{t('SeatMap:Next flight')}
				</BaseButton>
			)}
		</div>
	);

	const unavailableSeatMapMessage = (
		<div className={theme.unavailable} ref={unavailableMessageRef}>
			{currentSegment && (
				<img
					alt="company icon"
					className={theme.unavailable__img}
					src={currentSegment.segmentInfo.operatingAirline.icon}
				/>
			)}
			<p className={theme.unavailable__title}>
				{t('SeatMap:Unfortunately, seat selection on this flight is not available.')}
			</p>
			{props.segmentIndex + 1 < props.segments.length && (
				<BaseButton
					variant="optionAction"
					onClick={props.onSelectNextFlight}
					className={theme.unavailable__button}
				>
					{t('SeatMap:Next flight')}
				</BaseButton>
			)}
		</div>
	);

	return (
		<div
			className={cn({ [theme.seatMap_wide]: isSeatMapTooWide })}
			style={{ '--map-min-h': `${mapMinHeight}px` } as React.CSSProperties}
		>
			{canShowSeatMap(props.segmentIndex) && isSeatMapAvailable(props.segmentIndex) && (
				<MediaQuery maxWidth={TABLET_MIDDLE_WIDTH}>
					<div
						id="header"
						className={cn(theme.header_mobile, {
							[theme.header_mobile_overlay]: seatsRecommendContext.isOpen
						})}
					>
						<InfoMessages
							position={InfoMessagePositionsEnum.seat}
							className={theme.infoMessage}
							itemHtmlClassName={theme.infoMessage__htmlMessage}
						/>

						<Airplane
							aircraft={currentSegment.segmentInfo.aircraft}
							routeTime={currentSegment.segmentInfo.duration}
							sunInfo={currentSegment.sunSide}
							onSeatsDetailsClick={() => setSeatsOpen(true)}
							segment={currentSegment}
							isReturnFlight={props.isReturnFlight}
						/>

						<div id="passengers-container" className={theme.header_passengers}>
							{props.renderPassengers()}
						</div>
					</div>

					<Controls
						segmentId={props.segmentIndex}
						segmentsCount={props.segments.length}
						onProceed={props.onProceed}
						onSelectNextFlight={props.onSelectNextFlight}
						onBack={props.onBack}
					/>
				</MediaQuery>
			)}

			<MediaQuery minWidth={TABLET_MIDDLE_WIDTH + 1}>
				<InfoMessages
					position={InfoMessagePositionsEnum.seat}
					className={theme.infoMessage}
					itemHtmlClassName={theme.infoMessage__htmlMessage}
					contentClassName={theme.messages}
				/>
			</MediaQuery>

			<div className={cn(theme.map, { [theme.map_minHeight]: !isMobile })}>
				<MediaQuery minWidth={TABLET_MIDDLE_WIDTH + 1}>
					{canShowSeatMap(props.segmentIndex) && isSeatMapAvailable(props.segmentIndex) && (
						<div className={cn(theme.helpers, { [theme.helpers_recommend]: seatsRecommendContext.isOpen })}>
							<div>
								<Airplane
									aircraft={currentSegment.segmentInfo.aircraft}
									routeTime={currentSegment.segmentInfo.duration}
									sunInfo={currentSegment.sunSide}
									onSeatsDetailsClick={() => setSeatsOpen(true)}
									className={theme.airplane}
									isReturnFlight={props.isReturnFlight}
									segment={currentSegment}
								/>

								<div id="passengers-container" className={theme.passengers_container}>
									{props.renderPassengers()}
								</div>
							</div>

							<MediaQuery minWidth={+TABLET_MIN_WIDTH + 1}>
								<div className={theme.right}>
									{props.renderSeatsInfo()}
									<SeatsInfoAdditionalSlotOverride segmentIndex={props.segmentIndex} />
								</div>
							</MediaQuery>
						</div>
					)}
				</MediaQuery>

				<MediaQuery maxWidth={TABLET_MIN_WIDTH}>
					<MobileDialog
						classes={{ root: theme.seatsInfo__paper }}
						isOpen={seatsOpen}
						onClose={() => setSeatsOpen(false)}
						title={t('Seat details')}
						bgVariant="bg-fill"
					>
						<div className={theme.seatsInfo_content} data-test-id="seats-info-sheet">
							<>{props.renderSeatsInfo()}</>
							<SeatsInfoAdditionalSlotOverride segmentIndex={props.segmentIndex} />
						</div>
					</MobileDialog>
				</MediaQuery>

				{props.swipeBetweenSegments ? (
					<SwipableViews disabled={true} animateHeight={true} index={props.segmentIndex}>
						{props.segments.map((segment, index) => (
							<div key={segment.segmentInfo.id}>
								{isSeatMapAvailable(index) && canShowSeatMap(index) ? (
									<SeatMap
										allSeatServices={props.allSeatServices}
										services={props.services}
										gdsServiceById={props.gdsServiceById}
										seatMapRef={props.seatMapRef}
										isActive={props.segmentIndex === index}
										segment={segment}
										segmentId={index.toString()}
										onSelect={props.onSelect}
										passengers={props.passengers}
										passengerId={props.passengerId}
										activeDoubleSeats={isActiveDoubleSeat}
										isNewServicePredicate={props.isNewServicePredicate}
										onClear={props.onClear}
										activeFilters={props.activeFilters}
									/>
								) : segment.isSeatsAvailableInCheckinOnly && mode !== Mode.Checkin ? (
									availableOnCheckinMessage
								) : (
									unavailableSeatMapMessage
								)}
							</div>
						))}
					</SwipableViews>
				) : isSeatMapAvailable(props.segmentIndex) && canShowSeatMap(props.segmentIndex) ? (
					<SeatMap
						allSeatServices={props.allSeatServices}
						services={props.services}
						gdsServiceById={props.gdsServiceById}
						seatMapRef={props.seatMapRef}
						isActive={true}
						segmentId={props.segmentIndex.toString()}
						segment={props.segments[props.segmentIndex]}
						onSelect={props.onSelect}
						passengers={props.passengers}
						passengerId={props.passengerId}
						activeDoubleSeats={isActiveDoubleSeat}
						isNewServicePredicate={props.isNewServicePredicate}
						activeFilters={props.activeFilters}
					/>
				) : props.segments[props.segmentIndex].isSeatsAvailableInCheckinOnly && mode !== Mode.Checkin ? (
					availableOnCheckinMessage
				) : (
					unavailableSeatMapMessage
				)}
			</div>
		</div>
	);
});

export default SeatMapServiceContent;
